import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, ArrowRight, WarningCircle } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { MonthStatusBadge } from "./MonthDaysPanel";
import { currentPeriodIST, shiftPeriod, periodLabel, fmtHours } from "../../lib/regularisation";
import { formatDate } from "../../lib/format";

function MonthLine({ m, primary, onOpen }) {
    const pending = m.pending_dates?.length || 0;
    const mine = m.awaiting_employee || 0;
    return (
        <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 ${primary ? "" : "pt-3 border-t border-slate-100"}`} data-testid={`month-card-line-${m.period}`}>
            <div className="min-w-[150px]">
                <div className="text-sm font-semibold text-slate-900">{periodLabel(m.period)}</div>
                <MonthStatusBadge status={m.month_status} testId={`month-card-status-${m.period}`} />
            </div>
            <div className="text-xs text-slate-600 flex-1 min-w-[220px] space-y-0.5">
                <div>
                    <b className="text-slate-800">{m.days.worked_full_days}</b> full · <b className="text-slate-800">{m.days.worked_half_days}</b> half of {m.days.scheduled_working_days} scheduled days ·
                    {" "}{fmtHours(m.hours.approved_worked_hours)} vs {fmtHours(m.hours.expected_hours)} expected
                </div>
                {pending > 0 ? (
                    <div className="text-amber-800 flex items-center gap-1" data-testid={`month-card-pending-${m.period}`}>
                        <WarningCircle size={13} weight="fill" />
                        {mine > 0 ? `${mine} date${mine > 1 ? "s need" : " needs"} your regularisation` : ""}
                        {mine > 0 && m.awaiting_approver > 0 ? " · " : ""}
                        {m.awaiting_approver > 0 ? `${m.awaiting_approver} awaiting approval` : ""}
                        {m.month_ended && ` · submit by ${formatDate(m.deadlines.employee_deadline)}`}
                    </div>
                ) : (
                    <div className="text-emerald-700">{m.month_status === "Finalised" ? "Finalised for payroll" : "Nothing pending"}</div>
                )}
            </div>
            <Button size="sm" variant={primary ? "default" : "outline"} onClick={onOpen} data-testid={`month-card-open-${m.period}`}>
                {mine > 0 ? "Review & regularise" : "View month"} <ArrowRight size={13} className="ml-1" />
            </Button>
        </div>
    );
}

export function MonthSummaryCard() {
    const navigate = useNavigate();
    const [months, setMonths] = useState([]);
    useEffect(() => {
        const cur = currentPeriodIST();
        Promise.all([shiftPeriod(cur, -1), cur].map((p) => api.get("/attendance/my-month", { params: { period: p } }).then((r) => r.data).catch(() => null)))
            .then((rows) => setMonths(rows.filter(Boolean)));
    }, []);
    if (!months.length) return null;
    const [prev, cur] = months.length === 2 ? months : [null, months[0]];
    const ordered = prev && prev.month_status !== "Finalised" ? [prev, cur] : [cur, prev].filter(Boolean);
    return (
        <section className="mt-6 rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/70 to-white p-5" data-testid="month-summary-card">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <CalendarCheck size={18} className="text-indigo-600" weight="duotone" /> My Monthly Attendance
                </h2>
                <button type="button" className="text-xs text-indigo-700 hover:underline" onClick={() => navigate("/regularization/my")} data-testid="month-card-my-requests">My requests</button>
            </div>
            <div className="space-y-3">
                {ordered.map((m, i) => (
                    <MonthLine key={m.period} m={m} primary={i === 0} onOpen={() => navigate(`/attendance/my-month?period=${m.period}`)} />
                ))}
            </div>
        </section>
    );
}

export default MonthSummaryCard;
